import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import {
  GridFilterOperator,
  GridFilterInputValueProps,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { enumService } from '@/services/enumService';

let nextEnumFilterId = 0;

/**
 * Value option format for MUI DataGrid singleSelect columns
 */
export interface EnumValueOption {
  value: string;
  label: string;
}

/**
 * Format an enum value label, optionally prefixing with the ordinal number.
 */
function formatEnumLabel(displayName: string, ordinal: number, showNumericCode: boolean): string {
  return showNumericCode ? `${ordinal} - ${displayName}` : displayName;
}

/**
 * Hook to load enum value options at runtime.
 * Uses enumService which provides caching (memory + localStorage).
 * When the enum has showNumericCode enabled, labels include the ordinal prefix (e.g., "100 - Confirmed").
 */
export function useEnumValueOptions(enumType: string): {
  options: EnumValueOption[];
  loading: boolean;
  error: string | null;
} {
  const { t } = useTranslation();
  const [options, setOptions] = useState<EnumValueOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enumType) {
      setLoading(false);
      setError('enumType is required');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      enumService.getEnumValues(enumType),
      enumService.getEnumTypeMetadata(enumType),
    ])
      .then(([values, metadata]) => {
        if (!cancelled) {
          const showNumericCode = metadata?.showNumericCode ?? false;
          // Sort by ordinal and map to value/label format
          const sorted = [...values].sort((a, b) => a.ordinal - b.ordinal);
          setOptions(
            sorted.map((v) => {
              const translatedName = t(`Enum:${enumType}:${v.code}`, { defaultValue: v.displayName });
              return {
                value: v.code,
                label: formatEnumLabel(translatedName, v.ordinal, showNumericCode),
              };
            })
          );
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load enum values');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enumType, t]);

  return { options, loading, error };
}

/**
 * Hook to enhance grid columns with runtime-loaded enum valueOptions.
 *
 * @param columns - Base column definitions
 * @param enumTypes - Map of field name to enum type name
 * @returns Enhanced columns with valueOptions populated
 */
export function useEnumColumns(
  columns: any[],
  enumTypes: Record<string, string>
): any[] {
  // Load all required enum options
  const enumEntries = Object.entries(enumTypes);

  // Create a stable array of hooks results
  // Note: This is safe because enumTypes should be stable (defined at module level)
  const optionsResults = enumEntries.map(([, enumType]) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEnumValueOptions(enumType)
  );

  // Build options map from results
  const enumOptionsMap = useMemo(() => {
    const map: Record<string, EnumValueOption[]> = {};
    enumEntries.forEach(([field], index) => {
      map[field] = optionsResults[index].options;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(enumEntries), ...optionsResults.map(r => r.options)]);

  // Enhance columns with valueOptions
  return useMemo(() => {
    return columns.map((col) => {
      const options = enumOptionsMap[col.field];
      if (options && options.length > 0) {
        return { ...col, valueOptions: options };
      }
      return col;
    });
  }, [columns, enumOptionsMap]);
}

/**
 * Custom filter input for enum multi-select (isAnyOf operator).
 * Uses Select with multiple to match DataGrid filter panel styling.
 */
interface EnumFilterInputProps extends GridFilterInputValueProps {
  enumType: string;
}

function EnumMultiSelectFilterInput(props: EnumFilterInputProps) {
  const { item, applyValue, enumType, focusElementRef } = props;
  const { options, loading } = useEnumValueOptions(enumType);
  const selectRef = useRef<HTMLInputElement>(null);
  const [inputId] = useState(() => `enum-multi-filter-${nextEnumFilterId++}`);

  // Wire the DataGridPremium's focusElementRef to our input
  useEffect(() => {
    if (typeof focusElementRef === 'function') {
      focusElementRef(selectRef.current);
    } else if (focusElementRef && typeof focusElementRef === 'object') {
      (focusElementRef as React.MutableRefObject<HTMLInputElement | null>).current =
        selectRef.current;
    }
  }, [focusElementRef]);

  // Current selected values (array of codes)
  const selectedValues: string[] = useMemo(() => {
    if (!item.value) return [];
    if (Array.isArray(item.value)) return item.value;
    // Handle comma-separated string from backend
    if (typeof item.value === 'string') return item.value.split(',').filter(Boolean);
    return [];
  }, [item.value]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      // On autofill we get a stringified value
      const newValues = typeof value === 'string' ? value.split(',') : value;
      applyValue({ ...item, value: newValues });
    },
    [applyValue, item]
  );

  // Get display labels for selected values
  const renderValue = useCallback(
    (selected: string[]) => {
      if (selected.length === 0) return '';
      return selected
        .map((val) => options.find((o) => o.value === val)?.label || val)
        .join(', ');
    },
    [options]
  );

  return (
    <FormControl size="small" fullWidth sx={{ mt: '8px' }}>
      <InputLabel id={`${inputId}-label`}>
        Value
      </InputLabel>
      <Select
        labelId={`${inputId}-label`}
        id={inputId}
        multiple
        value={selectedValues}
        onChange={handleChange}
        inputRef={selectRef}
        renderValue={renderValue}
        label="Value"
      >
        {loading ? (
          <MenuItem disabled>Loading...</MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selectedValues.includes(option.value)} size="small" />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}

/**
 * Custom filter input for single enum value selection.
 * Uses Select to match DataGrid filter panel styling.
 */
function EnumSingleSelectFilterInput(props: EnumFilterInputProps) {
  const { item, applyValue, enumType, focusElementRef } = props;
  const { options, loading } = useEnumValueOptions(enumType);
  const selectRef = useRef<HTMLInputElement>(null);
  const [inputId] = useState(() => `enum-filter-${nextEnumFilterId++}`);

  // Wire the DataGridPremium's focusElementRef to our input
  useEffect(() => {
    if (typeof focusElementRef === 'function') {
      focusElementRef(selectRef.current);
    } else if (focusElementRef && typeof focusElementRef === 'object') {
      (focusElementRef as React.MutableRefObject<HTMLInputElement | null>).current =
        selectRef.current;
    }
  }, [focusElementRef]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      applyValue({ ...item, value: event.target.value });
    },
    [applyValue, item]
  );

  return (
    <FormControl size="small" fullWidth sx={{ mt: '8px' }}>
      <InputLabel id={`${inputId}-label`}>
        Value
      </InputLabel>
      <Select
        labelId={`${inputId}-label`}
        id={inputId}
        value={item.value || ''}
        onChange={handleChange}
        inputRef={selectRef}
        label="Value"
      >
        {loading ? (
          <MenuItem disabled>Loading...</MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}

/**
 * Returns filter operators for enum columns with runtime-loaded options.
 * Wraps built-in singleSelect operators with custom input components.
 */
export function getEnumFilterOperators(enumType: string): GridFilterOperator[] {
  const builtIn = getGridSingleSelectOperators();

  return builtIn.map((op) => {
    // Operators without value input (isEmpty, isNotEmpty) - keep as-is
    if (op.requiresFilterValue === false) {
      return op;
    }

    // isAnyOf needs multi-select input
    if (op.value === 'isAnyOf') {
      return {
        ...op,
        InputComponent: EnumMultiSelectFilterInput as any,
        InputComponentProps: { enumType },
      };
    }

    // Other operators (is, not) need single-select input
    return {
      ...op,
      InputComponent: EnumSingleSelectFilterInput as any,
      InputComponentProps: { enumType },
    };
  }) as GridFilterOperator[];
}
